import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import { faCheck, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ClassValue, clsx } from 'clsx';

import { useFloatingDropdown } from '@spacefill/shared/src/hooks/useFloatingDropdown';

import { cn } from '../../lib/utils';

export interface DropdownItemProps {
    disabled?: boolean;
    divider?: 'bottom' | 'top';
    className?: ClassValue;
    content: ReactNode;
    onClick?: () => void;
    href?: string;
    htmlAttributes?: HTMLAttributes<HTMLElement>;
    dataTestid?: string;
    slideContent?: ReactNode;
    slideClick?: () => void;
    slideBgClass?: string;
    selected?: boolean;
}

export interface DropdownProps {
    variant?: 'primary' | 'secondary' | 'outlined' | 'noOutline';
    size?: 'normal' | 'small';
    dataTestid?: string;
    buttonText?: string;
    iconStart?: ReactNode;
    iconEnd?: ReactNode;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    classNames?: string[];
    options: DropdownItemProps[];
    placement?: Placement;
    disabled?: boolean;
    inPortal?: boolean;
}

const getOptionClasses = (
    disabled: DropdownItemProps['disabled'],
    divider?: DropdownItemProps['divider'],
    className?: DropdownItemProps['className']
) =>
    cn(
        'flex items-center gap-1 justify-between whitespace-nowrap px-4 py-2 text-left text-sm leading-5 text-gray-400 drop-shadow-none first:rounded-t-[inherit] last:rounded-b-[inherit] focus:outline-none',
        {
            'text-gray-800 cursor-pointer hover:bg-gray-100 focus:bg-gray-100': !disabled,
            'cursor-not-allowed': disabled,
            'border-b border-gray-300 last:border-b-0': divider === 'bottom',
            'border-t border-gray-300 first:border-t-0': divider === 'top',
        },
        className
    );

// tingPlacementConfig(anchor: string): { placement: Placement; allowedPlacements: Placement[] } {
//     switch (anchor) {
//         case 'top start':
//             return { placement: 'top-start', allowedPlacements: ['top-start', 'bottom-start'] };
//         case 'top end':
//             return { placement: 'top-end', allowedPlacements: ['top-end', 'bottom-end'] };
//         case 'bottom end':
//             return { placement: 'bottom-end', allowedPlacements: ['bottom-end', 'top-end'] };
//         case 'top':
//             return {
//                 placement: 'top-start',
//                 allowedPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
//             };
//         case 'left':
//             return {
//                 placement: 'left-start',
//                 allowedPlacements: ['left-start', 'left-end', 'right-start', 'right-end'],
//             };
//         case 'right':
//             return {
//                 placement: 'right-start',
//                 allowedPlacements: ['right-start', 'right-end', 'left-start', 'left-end'],
//             };
//         case 'bottom':
//             return {
//                 placement: 'bottom-start',
//                 allowedPlacements: ['bottom-start', 'bottom-end', 'top-start', 'top-end'],
//             };
//         case 'bottom start':
//         default:
//             return { placement: 'bottom-start', allowedPlacements: ['bottom-start', 'top-start'] };
//     }
// }

export const Dropdown = ({
    variant = 'outlined',
    buttonText,
    iconStart,
    iconEnd,
    size = 'normal',
    dataTestid,
    htmlAttributes,
    classNames = [],
    options = [],
    placement = 'bottom-start',
    disabled = false,
    inPortal = true,
}: DropdownProps) => {
    const [slideOpenIdx, setSlideOpenIdx] = useState<number | null>(null);
    const {
        isOpen,
        setIsOpen,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        styles,
        isMounted,
        maxHeight,
    } = useFloatingDropdown({
        placement,
        offsetPx: 8,
    });

    useEffect(() => {
        if (!isOpen) {
            setSlideOpenIdx(null);
        }
    }, [isOpen]);

    const handleOptionClick = (onClick?: () => void) => {
        onClick?.();
        setIsOpen(false);
    };

    const floatingContent = (
        <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={-1}
        >
            <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, ...(maxHeight ? { maxHeight } : {}) }}
                {...getFloatingProps()}
                role='menu'
                className='z-dropdown overflow-y-auto'
            >
                {isMounted && (
                    <div
                        style={styles}
                        className='flex w-auto flex-col rounded-md border border-solid border-gray-300 bg-white text-sm shadow-lg'
                    >
                        {options.map((option, idx) => {
                            const {
                                disabled,
                                dataTestid,
                                htmlAttributes = {},
                                onClick,
                                content,
                                divider,
                                href,
                                className,
                                slideContent,
                                slideClick,
                                selected,
                            } = option;
                            if (!content) return null;

                            if (slideContent && slideClick) {
                                const transformStyle = {
                                    transform: slideOpenIdx === idx ? 'translateX(-50%)' : 'translateX(0%)',
                                } as const;
                                return (
                                    <div
                                        key={idx}
                                        data-testid={dataTestid}
                                        {...htmlAttributes}
                                        className='relative w-full overflow-hidden'
                                        role='none'
                                    >
                                        <div
                                            className='absolute inset-0 flex w-[200%] transition-transform duration-300 ease-out'
                                            style={transformStyle}
                                        >
                                            <div
                                                className={clsx(
                                                    'w-1/2 flex-none',
                                                    getOptionClasses(disabled, divider, className)
                                                )}
                                            >
                                                <button
                                                    type='button'
                                                    role='menuitem'
                                                    className='w-full text-left'
                                                    disabled={disabled}
                                                    onClick={() => {
                                                        if (disabled) return;
                                                        if (slideOpenIdx === idx) {
                                                            slideClick();
                                                        } else {
                                                            setSlideOpenIdx(idx);
                                                        }
                                                    }}
                                                >
                                                    {content}
                                                </button>
                                            </div>
                                            <div
                                                className={clsx(
                                                    'w-1/2 flex-none',
                                                    getOptionClasses(disabled, divider, className),
                                                    option.slideBgClass
                                                )}
                                            >
                                                <button
                                                    type='button'
                                                    role='menuitem'
                                                    className='w-full text-left'
                                                    disabled={disabled}
                                                    onClick={() => {
                                                        if (!disabled) {
                                                            slideClick();
                                                        }
                                                    }}
                                                >
                                                    {slideContent}
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            className={clsx(
                                                'invisible grid',
                                                getOptionClasses(disabled, divider, className)
                                            )}
                                            aria-hidden='true'
                                        >
                                            <div className='col-start-1 row-start-1'>{content}</div>
                                            <div className='col-start-1 row-start-1'>{slideContent}</div>
                                        </div>
                                    </div>
                                );
                            }

                            if (href && !disabled) {
                                return (
                                    <Link
                                        key={idx}
                                        data-testid={dataTestid}
                                        to={href}
                                        onClick={() => handleOptionClick(onClick)}
                                        {...htmlAttributes}
                                        role='menuitem'
                                        aria-selected={selected}
                                        className={getOptionClasses(disabled, divider, className)}
                                    >
                                        {content}
                                        {selected && (
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className='h-4 w-4 shrink-0 text-green-500'
                                            />
                                        )}
                                    </Link>
                                );
                            }

                            return (
                                <button
                                    key={idx}
                                    type='button'
                                    data-testid={dataTestid}
                                    onClick={() => handleOptionClick(onClick)}
                                    {...htmlAttributes}
                                    role='menuitem'
                                    aria-selected={selected}
                                    disabled={disabled}
                                    className={getOptionClasses(disabled, divider, className)}
                                >
                                    {content}
                                    {selected && (
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className='h-4 w-4 shrink-0 text-green-500'
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </FloatingFocusManager>
    );

    return (
        <div
            className={clsx([...classNames])}
            data-testid={dataTestid}
            data-component-name='Dropdown'
            {...htmlAttributes}
        >
            <div
                ref={refs.setReference}
                {...getReferenceProps()}
            >
                <button
                    type='button'
                    className={clsx(
                        'flex items-center gap-2 rounded-md text-sm h-full cursor-pointer',
                        size === 'small' ? 'px-2 py-1.25' : 'px-4 py-2',
                        {
                            'bg-lime-300 text-black hover:bg-lime-400 active:bg-lime-500 focus:outline-focus-primary disabled:bg-lime-100':
                                variant === 'primary',
                            'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 focus:outline-focus-primary text-black disabled:bg-gray-100':
                                variant === 'secondary',
                            'bg-white hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black border border-solid border-gray-300 disabled:bg-gray-100':
                                variant === 'outlined',
                            'bg-transparent hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black disabled:bg-gray-100':
                                variant === 'noOutline',
                            hidden: options.every((option) => option.content === false),
                            'cursor-not-allowed': disabled,
                        }
                    )}
                    disabled={disabled}
                    aria-expanded={isOpen}
                    aria-haspopup='menu'
                >
                    {iconStart}
                    {buttonText && <span>{buttonText}</span>}
                    {iconEnd ?? <FontAwesomeIcon icon={faChevronDown} />}
                </button>
            </div>
            {isOpen && (inPortal ? <FloatingPortal>{floatingContent}</FloatingPortal> : floatingContent)}
        </div>
    );
};
